import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _debounce from 'lodash/debounce';
import l10n from '../../lib/reactPPl10nHelper';
import { trackLink, trackPage } from '../../lib/analytics-helper';
import Page from '../../lib/page';
import poll from '../../lib/poll';
import Mutation from '../../lib/mutation-observer';
import { isNodeChatAlreadyExists } from '../../lib/globalHelper';
import { asyncOrigins, chatBotMessageUsRefIdsMapping } from '../../common/commonConstants';
import {
  initializeChatBot,
  isChatCardClicked, setChatBotClickInSession,
  setChatCardClickInSession, wasChatBotClicked,
} from '../../lib/chat-bot-live-helper';
import * as AsyncHelper from '../../lib/asyncMessageHelper';

const question = l10n.getContent('smarthelp/chatBot.message.question');
const bubbleText = l10n.getContent('smarthelp/chatBot.bubble.text');
const btnText = l10n.getContent('smarthelp/chatBot.button.text');
const rejectText = l10n.getContent('smarthelp/chatBot.bubble.reject');
const asyncPromptText = l10n.getContent('smarthelp/chatBot.asyncPrompt.question');
const asyncBubbleText = l10n.getContent('smarthelp/chatBot.asyncBubble.text');
const isLoggedIn = Page.isLoggedIn();

class ChatBot extends Component {
  static hidePrompt() { // eslint-disable-line class-methods-use-this
    if (isLoggedIn) {
      if (isNodeChatAlreadyExists()) {
        const chatNowBtn = document.getElementById('chat-now-btn');
        if (chatNowBtn) {
          chatNowBtn.style.display = 'none';
        }
      }
    }
  }

  static handleClose(e) { // eslint-disable-line class-methods-use-this
    const chatPromptCard = document.getElementById('chat-question-card');
    chatPromptCard.classList.add('fade-out');
    const chatNowBtn = document.getElementById('chat-now-btn');
    chatNowBtn.style.visibility = 'visible';
    chatNowBtn.style.position = 'relative';
    chatNowBtn.classList.add('btn-fadein');
    setChatCardClickInSession(true);
    chatPromptCard.style.position = 'absolute';
    chatPromptCard.style.left = '-99999px';
    e.cancelBubble = true; // otherwise, close will bubble up and invoke chat bot.
    if (e.stopPropagation) e.stopPropagation();
  }

  constructor(props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    if (this.props.display) {
      this.chatBotTracking();
      Mutation.waitForBodyToLoad(Mutation.nodeChatObserver);
    }
  }

  componentWillUpdate(nextProps) {
    if (this.props.display === false && nextProps.display === true) {
      this.chatBotTracking();
      Mutation.waitForBodyToLoad(Mutation.nodeChatObserver);
    }
  }

  componentDidUpdate() {
    ChatBot.hidePrompt();
  }

  getPromptMessage() {
    if (this.props.isChatBotAsyncUser) {
      return asyncPromptText;
    }
    return question;
  }

  getBubbleText() {
    if (this.props.isChatBotAsyncUser) {
      return asyncBubbleText;
    }
    return btnText;
  }

  chatBotTracking() {
    // Add expection for sending impression event on the final intent (L2, L3) topic tree in channel steering
    if (Page.isContactUsPage() && typeof this.props.topicRefId === 'string' && this.props.topicRefId !== chatBotMessageUsRefIdsMapping.GENPOOL_CONTACT_US) {
      return Promise.resolve();
    }

    const validateFpti = () => {
      if (!window.fpti) {
        return Promise.reject(false);
      }
      return Promise.resolve(window.fpti);
    };
    return poll(validateFpti, 4, 500)
      .then(() => trackPage({ link: this.props.link, id: this.props.id, hpid: this.props.hpid }))
      .catch(() => {});
  }

  chatBotPrompt() {
    return (
      <div // eslint-disable-line jsx-a11y/no-static-element-interactions
        id="chat-question-card"
        className="chat-question-card fade-in"
      >
        <button onClick={ChatBot.handleClose} className="close-btn" />
        <p className="ppl-logo" />
        <p className="chatbot-question">{this.getPromptMessage()}</p>
        <p className="chatbot-text">{bubbleText}</p>
        <button onClick={_debounce(this.handleClick, 2000)} className="chat-question-card-btn">
          <span className="chat-flex">
            <span className="chat-icon" aria-hidden="true" />
            <p className="btn-text">{this.getBubbleText()}</p>
          </span>
        </button>
        <button onClick={ChatBot.handleClose} className="reject-text">{rejectText}</button>
      </div>
    );
  }

  handleClick() {
    // Track chat bot click
    trackLink({ link: `${this.props.link}|clicked`, id: this.props.id, hpid: this.props.hpid });

    // Making chat card clicked true when user clicks on chatbot in both logout & login
    setChatCardClickInSession(true);

    if (!wasChatBotClicked() && !isLoggedIn) {
      setChatBotClickInSession(true);
    }

    // LOGIN LOGIC
    if (Page.isLoggedIn()) {
      // Pilot user checking
      // Launch MESSAGE US
      if (this.props.isChatBotAsyncUser) {
        return AsyncHelper.initiateAsyncChat({ topicRefId: this.props.topicRefId, origin: asyncOrigins.CHAT_PILL });
      }

      // NON ASYNC USER
      const chatBotParams = {
        topicRefId: this.props.topicRefId,
        link: this.props.link,
        hpid: this.props.hpid,
      };

      // Launch ChatBot
      return initializeChatBot(chatBotParams);
    }

    // Logged Out Logic
    return this.props.chatBotClicked(this.props.isChatBotAsyncUser);
  }

  hideChatBot() {
    return !this.props.display || Page.disableChatbotInMobileTablet() || this.props.isSearchInputBoxFocused();
  }

  render() {
    if (this.hideChatBot()) {
      return null;
    }

    const wasChatCardClicked = isChatCardClicked();
    const chatBtnClassName = !wasChatCardClicked ? 'chat-btn' : 'chat-btn btn-fadein';

    return (
      <div id="smarthelp-chatBot-prompt" className="chatBot-prompt">
        {!wasChatCardClicked && this.chatBotPrompt()}
        <button
          style={{ position: !wasChatCardClicked ? 'absolute' : 'relative' }}
          id="chat-now-btn"
          onClick={_debounce(this.handleClick, 2000)}
          className={chatBtnClassName}
        >
          <span className="chat-flex">
            <span className="chat-icon" aria-hidden="true" />
            <p className="btn-text">{this.getBubbleText()}</p>
          </span>
        </button>
      </div>
    );
  }
}

ChatBot.propTypes = {
  id: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  hpid: PropTypes.string.isRequired,
  display: PropTypes.bool.isRequired,
  chatBotClicked: PropTypes.func,
  topicRefId: PropTypes.string,
  isChatBotAsyncUser: PropTypes.bool.isRequired,
  isSearchInputBoxFocused: PropTypes.func,
};

ChatBot.defaultProps = {
  id: '',
  link: '',
  hpid: '',
  display: false,
  topicRefId: 'GENPOOL', // GENPOOL is generic topic id
  chatBotClicked: () => {},
  isSearchInputBoxFocused: () => {},
};

export default ChatBot;
